/** @format */

import { assetUrl } from "fusion-core";
import { Helmet } from "fusion-plugin-react-helmet-async";
import { useForm } from "react-hook-form";
import QuoteFormWrapper from "../../../shared/components/quoteFormWrapper";
import FormNavigationButtons from "../../../shared/components/FormNavigationButtons";
import QuoteStepHeading from "../../../shared/components/QuoteStepHeading";
import QuoteStepLoader from "../../../shared/components/QuoteStepLoader";
import Button from "../../../shared/components/Button";
import { TAXI_QUOTE_BRANDREF } from "../../../config/config";
import { useEffect, useRef, useState } from "react";
import Switch from "react-switch";
import BuyStep from "../enums/BuyStep";
import QuoteStep from "../../quote/enums/QuoteStep";
import useQuoteStore from "../../quote/store/QuoteStore";
import { handleNext } from "../../../shared/utils/navigationUtils";
import { useNavigate } from "fusion-plugin-react-router";
import {
  fetchTemplateList,
  getAddonDocumentDownloadURL,
  saveMember,
} from "../services/buyService";
import useBuyStore from "../store/BuyStore";
import { handleDownloadPDF } from "../../../shared/utils/apiUtils";
import { HotjarEventNames } from "../../../shared/utils/hotjarEventNames";
import { TrackHotjarEvent } from "../../../shared/utils/trackHotjarEvent";
import useAppStateStore from "../../../shared/store/AppStateStore";

const TbAddonRecommendations = () => {
  // Form Setup
  const { setValue, handleSubmit } = useForm();

  let STATE = useAppStateStore();

  const navigate = useNavigate();
  const buyStore = useBuyStore.getState();
  const recommendationsData = buyStore.getObject("recommendationsData");
  const addonsDescriptions = buyStore.getObject("addonDescriptions");

  const [selectedAddons, setSelectedAddons] = useState(
    new Set(buyStore.getObject("selectedAddons") || []),
  );

  // Constants
  const css = assetUrl("../../../styles/main.css");
  const brandRef = TAXI_QUOTE_BRANDREF;
  const riskObjectId = useQuoteStore.getState().quote?.risk?.riskObjectId;
  const previousRecommendationsData = useRef(null);
  const selectedSchemeTableId = STATE.selectedSchemeTableId;

  // Initialize form data
  useEffect(() => {
    const storedAddons = buyStore.getObject("selectedAddons");
    if (storedAddons) {
      setSelectedAddons(new Set(storedAddons));
      setValue("addedAddons", new Set(storedAddons));
      setValue("recommendationsAddedAddons", storedAddons);
    }
  }, []);

  const toggleAddon = (addonId, addonIPIDTriggerUrl) => {
    const newAddedAddons = new Set(selectedAddons);
    if (newAddedAddons.has(addonId)) {
      newAddedAddons.delete(addonId);
    } else {
      newAddedAddons.add(addonId);
      if (addonIPIDTriggerUrl) {
        getAddonDocumentDownloadURL(addonIPIDTriggerUrl, navigate);
      }
    }
    setSelectedAddons(newAddedAddons);
    buyStore.setObject("selectedAddons", Array.from(newAddedAddons));
    setValue("addedAddons", newAddedAddons);
    setValue("recommendationsAddedAddons", Array.from(newAddedAddons));
    setValue("selectedOption", undefined);
  };

  useEffect(() => {
    const hasDataChanged =
      JSON.stringify(previousRecommendationsData.current) !==
      JSON.stringify(recommendationsData);

    const vaultRequestBody = {
      BrandRef: brandRef,
      riskObjectId: riskObjectId,
      riskObjectHistoryId: 1,
      documentTagStage: "Quote",
      documentTagCategory: "PreSaleQuoteDocuments",
    };

    buyStore.setObject("vaultRequestBody", vaultRequestBody);

    // Get the array of selected addon IDs
    const selectedAddonArray = Array.from(selectedAddons);

    const saveMemberRequestBody = {
      RiskObjectId: riskObjectId,
      RiskObjectHistoryId: "1",
      BrandRef: brandRef,
      PreSelectedQuoteAddons: selectedAddonArray,
      SelectedSchemeTableId: selectedSchemeTableId || "",
      UseSessionData: hasDataChanged,
    };
    buyStore.setObject("saveMemberRequestBody", saveMemberRequestBody);

    previousRecommendationsData.current = recommendationsData;
  }, [
    selectedAddons,
    recommendationsData,
    riskObjectId,
    selectedSchemeTableId,
    brandRef,
  ]);

  const [loadingStates, setLoadingStates] = useState({});

  const handleDownloadClick = async (e, link, name, index) => {
    e.stopPropagation();
    setLoadingStates((prev) => ({ ...prev, [index]: true }));
    try {
      await handleDownloadPDF(navigate, link, name);
    } finally {
      setLoadingStates((prev) => ({ ...prev, [index]: false }));
    }
  };

  useEffect(() => {
    TrackHotjarEvent(
      HotjarEventNames.TAXI_QUOTE_TBADDONRECOMMENDATIONNS_ENTERED,
    );
  }, []);

  const renderAddons = () => {
    if (!recommendationsData) return null;

    return recommendationsData.map((addon, index) => {
      const isSelected = selectedAddons.has(addon.productAddonId);
      const monthlyPremium = (addon.addonGrossPremiumIncIPT / 12).toFixed(2);
      const annualPremium = addon.addonGrossPremiumIncIPT.toFixed(2);
      const isLoading = loadingStates[index] || false;

      const addonDescription =
        addonsDescriptions?.find(
          (desc) => desc.addonId === addon.productAddonId,
        )?.description || "";

      return (
        <div
          role="button"
          tabIndex={0}
          aria-pressed={isSelected}
          onClick={() =>
            toggleAddon(addon.productAddonId, addon.addonIPIDTriggerUrl)
          }
          onKeyDown={(event) => {
            if (
              event.target === event.currentTarget &&
              (event.key === "Enter" || event.key === " ")
            ) {
              event.preventDefault();
              toggleAddon(addon.productAddonId, addon.addonIPIDTriggerUrl);
            }
          }}
          className={`tw-mb-4 tw-w-full tw-cursor-pointer tw-rounded-dis-card tw-border-solid tw-bg-white tw-transition-colors hover:tw-border-dis-deep-blue ${
            isSelected
              ? "tw-border-2 tw-border-dis-deep-blue tw-bg-dis-sky-tint tw-p-[19px]"
              : "tw-border tw-border-dis-border-strong tw-p-5"
          }`}
          key={addon.productAddonId}
        >
          <div className="tw-flex tw-items-start tw-justify-between tw-gap-4">
            <div className="tw-min-w-0">
              <h2 className="tw-m-0 tw-text-base tw-font-bold tw-leading-[1.4] tw-text-dis-navy">
                {addon.addonSchemeName}
              </h2>
              <p className="tw-m-0 tw-mt-1 tw-text-base tw-leading-[1.5] tw-text-dis-text-muted">
                {addon.supplier}
              </p>
            </div>
            <div onClick={(event) => event.stopPropagation()}>
              <Switch
                width={50}
                height={30}
                handleDiameter={26}
                offColor="#EAF1F6"
                onColor="#0B6694"
                checkedIcon={false}
                uncheckedIcon={false}
                checked={isSelected}
                aria-label={`Add ${addon.addonSchemeName}`}
                onChange={() =>
                  toggleAddon(addon.productAddonId, addon.addonIPIDTriggerUrl)
                }
              />
            </div>
          </div>

          <p className="tw-mb-0 tw-mt-4 tw-text-base tw-leading-[1.5] tw-text-dis-text-2">
            {addonDescription}
          </p>

          <div className="tw-mt-4 tw-flex tw-flex-wrap tw-gap-2">
            <div className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-dis-sky-tint tw-px-3 tw-py-2 tw-text-dis-small tw-leading-[1.25] tw-text-dis-deep-blue">
              <span className="tw-font-semibold">Annual: £{annualPremium}</span>
            </div>
            <div className="tw-inline-flex tw-items-center tw-rounded-md tw-bg-dis-sky-tint tw-px-3 tw-py-2 tw-text-dis-small tw-leading-[1.25] tw-text-dis-deep-blue">
              <span className="tw-font-semibold">
                Monthly: £{monthlyPremium}
              </span>
            </div>
          </div>

          <div className="tw-mt-3">
            <Button
              type="button"
              variant="soft"
              size="small"
              className="max-[520px]:tw-w-full"
              disabled={isLoading}
              loading={isLoading}
              loadingText="Loading"
              onClick={(e) =>
                handleDownloadClick(
                  e,
                  addon.policyWordingGetUrl,
                  addon.policyWordingDownloadFileName,
                  index,
                )
              }
            >
              Policy wording
            </Button>
          </div>
        </div>
      );
    });
  };
  const onSubmit = async () => {
    STATE.setDocumentsLoading(true);
    TrackHotjarEvent(
      HotjarEventNames.TAXI_QUOTE_TBADDONRECOMMENDATIONNS_NEXT_CLICKED,
    );
    try {
      await saveMember(buyStore.getObject("saveMemberRequestBody"));
      const response = await fetchTemplateList(
        buyStore.getObject("vaultRequestBody"),
        navigate,
      );
      buyStore.setObject("vaultResponse", response);
    } catch (error) {
      console.error("Error in addon recommendations submission:", error);
    } finally {
      STATE.setDocumentsLoading(false);
    }
    handleNext(BuyStep.TB_DOCUMENTS, STATE);
  };

  if (STATE.documentsLoading) {
    return (
      <QuoteStepLoader
        title="Your Documents"
        description="Please take your time to read through and understand your documents."
        message="We are just fetching your documentation."
      />
    );
  }

  return (
    <>
      <Helmet>
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=0"
        />
        <link rel="stylesheet" href={css} />
      </Helmet>
      <form onSubmit={handleSubmit(onSubmit)}>
        <QuoteFormWrapper>
          <div className="">
            <QuoteStepHeading
              title={recommendationsData?.title || "Add-ons available to you"}
              description="All add-ons include Insurance Premium Tax, and the monthly price is equivalent to the annual cost divided by 12 months, excluding interest."
            />
            {STATE.recommendationsLoading ? (
              <LuLoaderCircle
                className="tw-h-6 tw-w-6 tw-animate-spin tw-text-dis-deep-blue"
                aria-label="Loading add-ons"
              />
            ) : recommendationsData?.length > 0 ? (
              <div>
                {renderAddons()}
                <div className="tw-mt-4 tw-border-t tw-border-dis-border tw-pt-4 tw-text-dis-min tw-leading-[1.5] tw-text-dis-text-muted">
                  <p className="tw-m-0">
                    *These examples are provided for illustrative purposes only.
                    Please refer to the policy wording for terms and conditions.
                  </p>
                </div>
              </div>
            ) : (
              <p>No add-ons available</p>
            )}
          </div>
        </QuoteFormWrapper>

        <FormNavigationButtons prevStep={QuoteStep.TQ_PANEL} isValid={true} />
      </form>
    </>
  );
};

export default TbAddonRecommendations;
